import { entityName, isKnowRole, SPAN_ROLES, spanRoleOf, type SpanRole, type StoryNode } from '@dispositio/core';
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { de } from '../../i18n/de';
import { designFoci } from '../../lib/designModel';
import { linkPresetOf, shortcutOf } from '../../lib/linkShortcuts';
import { browseRefs, type BrowseItem } from '../../lib/refBrowser';
import { usePinnedIds, useRefPins } from '../../state/refPins';
import { useStoryEnv } from '../../state/storyEnv';
import { FocusIcon } from '../design/DesignView';
import { EditorIcon } from './EditorIcons';
import { AssetImg } from '../AssetImg';

/** Nennung (das Wort meint das Element) oder Bezug (die Stelle gehört zu Elementen) */
export type RefKind = 'name' | 'span';

interface Props {
  /** markierter Text: Treffer dazu stehen oben, „Neu anlegen“ übernimmt ihn als Namen; leer: an der Stelle kommt ein Verweis (mit „@“) */
  text: string;
  /** Anfang der Suche (mit „@“ schon Getipptes) */
  query?: string;
  /** Szene bzw. Node des Textes: seine Einträge stehen vor den übrigen */
  node?: StoryNode;
  /** Schlüssel, mit dem die Stelle schon verknüpft ist (dann: umhängen oder lösen) */
  linkedKey: string | null;
  /** Weitere, noch nicht verknüpfte Stellen mit demselben Text im Dokument */
  others: number;
  /** Lage der Markierung im Fenster */
  anchor: { left: number; top: number; bottom: number };
  /** Art beim Öffnen; `kinds`: umschaltbar zwischen „Ist …“ und „Gehört zu …“ (nur mit Markierung in einem Dokument) */
  kind?: RefKind;
  kinds?: boolean;
  /** Art des Bezugs beim Öffnen (über ein Tastenkürzel gewählt); sonst die eines bestehenden Bezugs bzw. nach der Markierung */
  role?: SpanRole;
  /** Bezug, der an der Stelle schon besteht */
  span?: { keys: string[]; role: SpanRole | null } | null;
  /** Favoriten (IDs der Einträge, z. B. die im Dokument schon vorkommenden): ganz oben; leer: kein eigener Abschnitt */
  favorites: string[];
  onPick: (refKey: string, all: boolean) => void;
  /** Bezug setzen (bzw. ändern) */
  onSpan?: (keys: string[], role: SpanRole | null) => void;
  onUnspan?: () => void;
  /** `name`: die Markierung bzw. ohne sie das Gesuchte; `span`: als Bezug (mit den schon gewählten Elementen) */
  onCreate: (typeId: string, all: boolean, name: string, span: { keys: string[]; role: SpanRole | null } | null) => void;
  onUnlink: () => void;
  /** `back`: mit Esc bzw. ✕ geschlossen – der Cursor kehrt an die Stelle im Text zurück */
  onClose: (back: boolean) => void;
}

const WIDTH = 460;
const HEIGHT = 600;

/**
 * Referenz-Browser: markierten Text (bzw. das Wort vor einem getippten „@“) mit einem Eintrag verknüpfen oder an der Stelle des „@“ einen
 * Verweis einfügen. Ganz oben die Favoriten, dann was zur Markierung passt, dann die Einträge der Szene, dann alle; filtern nach Klasse
 * und Suche, Bilder und Farben zum schnellen Erkennen. Vorgewählt (Enter) ist, was am besten zur Markierung passt. Fehlt der Eintrag
 * noch, legt man ihn hier gleich an.
 *
 * Mit Markierung in einem Dokument wählt man oben zuerst die Art: „Ist …“ (das Wort ist das Element, ein Klick verknüpft) oder
 * „Gehört zu …“ (die Stelle gehört zu einem oder mehreren Elementen: darunter Rede, Handlung, Gedanke, Beschreibung; ankreuzen,
 * „Zuordnen“). Vorausgewählt nach der Markierung bzw. dem Tastenkürzel, mit dem der Browser geöffnet wurde; die Kürzel (Strg+⇧+1 … 5)
 * schalten auch hier um.
 *
 * Ganz rechts an jedem Element heftet die Nadel es rechts neben den Text (siehe RefPins): ein Klick dort verknüpft ohne diesen Browser.
 */
export function RefBrowser({ text, query: initialQuery = '', node, linkedKey, others, anchor, kind: initialKind = 'name', kinds = false, role: initialRole, span = null, favorites, onPick, onSpan, onUnspan, onCreate, onUnlink, onClose }: Props) {
  const { ctx, projectId } = useStoryEnv();
  const pinned = usePinnedIds(projectId);
  const togglePin = useRefPins((s) => s.toggle);
  const host = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(initialQuery);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [all, setAll] = useState(false);
  const [kind, setKind] = useState<RefKind>(initialKind);
  // Bezug: angekreuzte Elemente und Art (in Anführungszeichen: Rede)
  const [picked, setPicked] = useState<string[]>(span?.keys ?? []);
  // per Kürzel gewählt; sonst neu und nicht in Anführungszeichen: Beschreibung; ein älterer Bezug ohne Art behält sie, bis man eine wählt
  const [role, setRole] = useState<SpanRole | null>(initialRole ?? (span ? span.role : (spanRoleOf(text) ?? 'beschreibung')));
  const spanning = kind === 'span';
  const { sections, classes } = useMemo(() => browseRefs(ctx, { text, query, typeId, node, favorites }), [ctx, text, query, typeId, node, favorites]);
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  // vorgewählt: was am besten zur Markierung passt (die Favoriten darüber passen womöglich gar nicht), sonst das erste
  const best = useMemo(() => flat.reduce((b, it, i) => (it.score > flat[b]!.score ? i : b), 0), [flat]);
  const linked = linkedKey ? ctx.story.entities.find((e) => e.refKey === linkedKey || e.aliases?.includes(linkedKey)) : undefined;
  // Klassen zum Neuanlegen: die Bereiche der Bibliothek (auch ohne Einträge)
  const creatable = useMemo(() => designFoci(ctx).filter((f) => f.key !== 'world'), [ctx]);
  const nameOfKey = (key: string): string => {
    const e = ctx.story.entities.find((x) => x.refKey === key);
    return (e && entityName(e, ctx.types.get(e.typeId))) || `@${key}`;
  };

  useEffect(() => {
    setActive(best);
  }, [query, typeId]); // eslint-disable-line react-hooks/exhaustive-deps
  // schon Getipptes: weiter tippen hinter dem Gesuchten
  useEffect(() => {
    const el = search.current;
    if (el) el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active, flat]);
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (host.current && !host.current.contains(e.target as Node)) onClose(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [onClose]);

  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - WIDTH - 8));
  const below = anchor.bottom + 8;
  const top = below + HEIGHT > window.innerHeight - 8 ? Math.max(8, anchor.top - HEIGHT - 8) : below;
  const short = text.length > 42 ? `${text.slice(0, 40)}…` : text;
  const heading = spanning ? de.refBrowser.spanTitle(short) : text ? de.refBrowser.title(short) : de.refBrowser.insertTitle;
  // Name eines neuen Eintrags: die Markierung, ohne sie das Gesuchte (beim Bezug nur das Gesuchte – die Stelle ist kein Name)
  const name = spanning ? query.trim() : text || query.trim();
  const shortName = name.length > 42 ? `${name.slice(0, 40)}…` : name;
  const toggle = (key: string): void => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  const choose = (it: BrowseItem): void => (spanning ? toggle(it.refKey) : onPick(it.refKey, all));
  const assign = (): void => {
    if (picked.length > 0) onSpan?.(picked, role);
  };
  let index = -1;

  const item = (it: BrowseItem) => {
    index += 1;
    const i = index;
    const on = spanning && picked.includes(it.refKey);
    const isPinned = pinned.includes(it.entityId);
    return (
      <div key={it.entityId} className={`rb-row${i === active ? ' active' : ''}`} role="none" onMouseEnter={() => setActive(i)}>
        <button
          ref={i === active ? activeRef : undefined}
          type="button"
          role="option"
          aria-selected={spanning ? on : i === active}
          className={`rb-item${i === active ? ' active' : ''}${!spanning && it.refKey === linkedKey ? ' current' : ''}${on ? ' picked' : ''}`}
          onClick={() => choose(it)}
          data-testid="rb-item"
          data-ref={it.refKey}
        >
          {spanning && <span className={`rb-check${on ? ' on' : ''}`} aria-hidden="true">{on ? '✓' : ''}</span>}
          <span className="rb-avatar" style={{ '--c': it.typeColor } as CSSProperties}>
            {it.imageId ? <AssetImg id={it.imageId} alt="" loading="lazy" /> : <span>{it.title.slice(0, 1).toUpperCase()}</span>}
          </span>
          <span className="rb-main">
            <span className="rb-title">{it.title}</span>
            <span className="rb-sub">
              <span className="rb-dot" style={{ background: it.typeColor }} aria-hidden="true" />
              {it.typeName}
              {it.text && <span className="rb-text"> · {it.text}</span>}
            </span>
          </span>
          {!spanning && it.refKey === linkedKey && <span className="badge info">{de.refBrowser.current}</span>}
        </button>
        <button
          type="button"
          className={`rb-pin${isPinned ? ' on' : ''}`}
          aria-pressed={isPinned}
          aria-label={isPinned ? de.refBrowser.unpin(it.title) : de.refBrowser.pin(it.title)}
          title={isPinned ? de.refBrowser.unpin(it.title) : de.refBrowser.pin(it.title)}
          // der Fokus bleibt in der Suche (Tastatur)
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => togglePin(projectId, it.entityId)}
          data-testid="rb-pin"
          data-ref={it.refKey}
        >
          <EditorIcon kind="pin" size={15} />
        </button>
      </div>
    );
  };

  return (
    <div
      ref={host}
      className="ref-browser"
      style={{ left, top, width: WIDTH, maxHeight: HEIGHT }}
      role="dialog"
      aria-label={heading}
      data-testid="ref-browser"
      data-kind={kind}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onClose(true);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (flat.length > 0) setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : -1) + flat.length) % flat.length);
        } else if (e.key === 'Enter' && spanning && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          assign();
        } else if (linkPresetOf(e)) {
          // Strg+⇧+1 … 5: Art umschalten (nur wo es die Wahl gibt)
          e.preventDefault();
          const preset = linkPresetOf(e)!;
          if (!kinds) return;
          setKind(preset.kind);
          if (preset.kind === 'span') setRole(preset.role);
        } else if (e.key === 'Enter' && e.target instanceof HTMLInputElement && e.target.type === 'search') {
          e.preventDefault();
          const it = flat[Math.min(active, flat.length - 1)];
          if (it) choose(it);
        }
      }}
    >
      <div className="rb-head">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1.2 1.2" />
          <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1.2-1.2" />
        </svg>
        <strong className="grow rb-heading">{heading}</strong>
        <button type="button" className="btn ghost icon" onClick={() => onClose(true)} aria-label={de.common.close} data-testid="rb-close">
          ✕
        </button>
      </div>
      {/* zuerst die Art: „Ist …“ oder „Gehört zu …“ (dann gleich darunter Rede, Handlung, Gedanke); abgesetzt vom Rest */}
      {kinds && (
        <div className="rb-kind-block">
          <div className="seg rb-kinds" role="group" aria-label={de.refBrowser.kinds}>
            {(['name', 'span'] as const).map((k) => (
              <button
                key={k}
                type="button"
                className={`seg-btn${kind === k ? ' active' : ''}`}
                aria-pressed={kind === k}
                title={k === 'name' ? de.refBrowser.kindNameHint(shortcutOf({ kind: 'name' })!) : de.refBrowser.kindSpanHint(SPAN_ROLES.map((r) => de.refBrowser.roleHint(de.refBrowser.roles[r]!, shortcutOf({ kind: 'span', role: r }))).join(', '))}
                onClick={() => setKind(k)}
                data-testid={`rb-kind-${k}`}
              >
                {k === 'name' ? de.refBrowser.kindName : de.refBrowser.kindSpan}
              </button>
            ))}
          </div>
          {spanning && (
            <div className="seg rb-roles" role="group" aria-label={de.refBrowser.role}>
              {SPAN_ROLES.filter((r) => !isKnowRole(r)).map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`seg-btn${role === r ? ' active' : ''}`}
                  aria-pressed={role === r}
                  title={de.refBrowser.roleHint(de.refBrowser.roles[r]!, shortcutOf({ kind: 'span', role: r }))}
                  onClick={() => setRole(r)}
                  data-testid={`rb-role-${r}`}
                >
                  {de.refBrowser.roles[r]}
                </button>
              ))}
            </div>
          )}
          {spanning && (
            <div className="rb-know">
              <span className="small muted">{de.refBrowser.know}</span>
              <div className="seg rb-roles" role="group" aria-label={de.refBrowser.know}>
                {SPAN_ROLES.filter(isKnowRole).map((r) => (
                  <button key={r} type="button" className={`seg-btn${role === r ? ' active' : ''}`} aria-pressed={role === r} title={de.refBrowser.knowHints[r]} onClick={() => setRole(r)} data-testid={`rb-role-${r}`}>
                    {de.refBrowser.roles[r]}
                  </button>
                ))}
              </div>
            </div>
          )}
          {spanning && role && isKnowRole(role) && (
            <div className="small muted rb-know-hint" data-testid="rb-know-hint">
              {de.refBrowser.knowHints[role]}
            </div>
          )}
        </div>
      )}
      {!spanning && linked && (
        <div className="rb-linked small">
          {de.refBrowser.linkedTo(entityName(linked, ctx.types.get(linked.typeId)) || linked.refKey)}
          <button type="button" className="btn small ghost danger-text" onClick={onUnlink} data-testid="rb-unlink">
            {de.refBrowser.unlink}
          </button>
        </div>
      )}
      {spanning && span && (
        <div className="rb-linked small">
          {de.refBrowser.spanOf(span.keys.map(nameOfKey).join(', '))}
          <button type="button" className="btn small ghost danger-text" onClick={onUnspan} data-testid="rb-unspan">
            {de.refBrowser.unspan}
          </button>
        </div>
      )}
      <input ref={search} type="search" autoFocus className="rb-search" placeholder={de.refBrowser.search} aria-label={de.refBrowser.search} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="rb-search" />
      <div className="rb-filters" role="group" aria-label={de.refBrowser.filter}>
        <button type="button" className={`rb-chip${typeId === null ? ' on' : ''}`} aria-pressed={typeId === null} onClick={() => setTypeId(null)} data-testid="rb-filter-all">
          {de.refBrowser.all}
        </button>
        {classes.map((c) => (
          <button key={c.id} type="button" className={`rb-chip${typeId === c.id ? ' on' : ''}`} aria-pressed={typeId === c.id} onClick={() => setTypeId(typeId === c.id ? null : c.id)} data-testid="rb-filter">
            <span className="rb-dot" style={{ background: c.color }} aria-hidden="true" />
            {c.name} <span className="muted">{c.count}</span>
          </button>
        ))}
      </div>
      <div className="rb-list" role="listbox" aria-label={heading} aria-multiselectable={spanning || undefined}>
        {flat.length === 0 && <div className="muted small rb-none">{de.refBrowser.none}</div>}
        {sections.map((s) =>
          s.items.length === 0 ? null : (
            <div key={s.key} className="rb-section" role="group" aria-label={de.refBrowser.sections[s.key]}>
              {(sections.filter((x) => x.items.length > 0).length > 1 || s.key !== 'all') && <div className="rb-section-title">{de.refBrowser.sections[s.key]}</div>}
              {s.items.map(item)}
            </div>
          ),
        )}
      </div>
      <div className="rb-new">
        <span className="small muted">{name ? de.refBrowser.newAs(shortName) : de.refBrowser.newPlain}</span>
        <div className="rb-new-types">
          {creatable.map((f) => (
            <button
              key={f.key}
              type="button"
              className="rb-new-type"
              onClick={() => f.type && onCreate(f.type.id, all, name, spanning ? { keys: picked, role } : null)}
              disabled={!f.type}
              title={name ? de.refBrowser.newHint(shortName, f.type?.name ?? f.label) : de.refBrowser.newPlainHint(f.type?.name ?? f.label)}
              data-testid="rb-new"
              data-type={f.type?.name ?? f.label}
            >
              <FocusIcon focus={f} size={16} />
              {f.type?.name ?? f.label}
            </button>
          ))}
        </div>
      </div>
      {spanning ? (
        <div className="rb-assign">
          <button type="button" className="btn small primary" disabled={picked.length === 0} onClick={assign} data-testid="rb-assign">
            {de.refBrowser.assign(picked.length)}
          </button>
        </div>
      ) : (
        others > 0 && (
          <label className="check small rb-all">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} data-testid="rb-all" />
            <span>{de.refBrowser.alsoOthers(others, short)}</span>
          </label>
        )
      )}
      <div className="muted small rb-hint">{spanning ? de.refBrowser.spanHint : de.refBrowser.hint}</div>
    </div>
  );
}
